import { buildApiUrl } from '../../utils/apiUrl';
import { appelAuthentifie, extensionDuType, requete } from './assistants';

export type EtatExecution = 'attente' | 'en_cours' | 'terminee' | 'echouee';

export interface ExecutionResume {
  id: number;
  agent: number;
  agent_nom: string;
  declenchement: 'planifiee' | 'manuelle';
  periode: string;
  etat: EtatExecution;
  avancement: string;
  erreur: string;
  lancee_le: string;
  terminee_le: string | null;
  a_production: boolean;
  /** Durée du passage, en secondes, une fois terminé. */
  duree: number | null;
}

export interface DocumentAgent extends ExecutionResume {
  titre: string;
  format: string | null;
}

export interface ExecutionDetail extends ExecutionResume {
  contenu: string;
  payload: any;
  format: string | null;
  conversation: number | null;
  peut_poursuivre: boolean;
}

export type Frequence = 'quotidienne' | 'hebdomadaire' | 'mensuelle' | 'sur_demande';

/** Où en est l'agent dont un autre dépend, pour la période en cours. */
export interface Prerequis {
  agent: number;
  nom: string;
  periode: string;
  fait: boolean;
  termine_le: string | null;
  execution: number | null;
}

export interface AgentCowork {
  id: number;
  nom: string;
  objectif: string;
  sortie: string;
  frequence: Frequence;
  frequence_libelle: string;
  /** Jour du mois (1 à 28) en mensuel, de la semaine (0 = lundi) en hebdomadaire. */
  jour: number;
  /** Heure de Conakry. */
  heure: number;
  actif: boolean;
  /** L'équipe OmnIA lui a donné un assistant : il peut travailler. */
  configure: boolean;
  assistant_titre: string;
  projet_nom: string;
  rafraichir_chiffres: boolean;
  prochaine_execution: string | null;
  derniere_execution: string | null;
  dernier_etat: EtatExecution | null;
  executions: ExecutionResume[];
  prerequis: Prerequis | null;
  periode_courante: string;
}

export const coworkService = {
  listerAgents() {
    return requete<{ agents: AgentCowork[]; contact: string }>('/documents/cowork/agents/');
  },

  journal() {
    return requete<{ journal: ExecutionResume[]; documents: DocumentAgent[] }>(
      '/documents/cowork/agents/journal/',
    );
  },

  chargerAgent(id: number) {
    return requete<AgentCowork>(`/documents/cowork/agents/${id}/`);
  },

  /**
   * Lance l'agent. Si les données dont il dépend ne sont pas vérifiées, le
   * serveur demande confirmation (428) : on rend alors l'état du prérequis.
   */
  async lancer(id: number, sansVerification = false): Promise<
    { lance: ExecutionResume } | { aConfirmer: Prerequis; detail: string }
  > {
    const reponse = await appelAuthentifie(buildApiUrl(`/documents/cowork/agents/${id}/lancer/`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sansVerification ? { sans_verification: true } : {}),
    });
    const corps = await reponse.json().catch(() => ({}));
    if (reponse.status === 428) return { aConfirmer: corps.prerequis, detail: corps.detail };
    if (!reponse.ok) throw new Error(corps.detail || `Erreur ${reponse.status}`);
    return { lance: corps as ExecutionResume };
  },

  calendrier(id: number, champs: { frequence: Frequence; jour: number; heure: number }) {
    return requete<AgentCowork>(`/documents/cowork/agents/${id}/calendrier/`, {
      method: 'POST',
      body: JSON.stringify(champs),
    });
  },

  basculer(id: number) {
    return requete<AgentCowork>(`/documents/cowork/agents/${id}/basculer/`, { method: 'POST' });
  },

  chargerExecution(id: number) {
    return requete<ExecutionDetail>(`/documents/cowork/executions/${id}/`);
  },

  async exporter(id: number, nomRepli: string) {
    const reponse = await appelAuthentifie(buildApiUrl(`/documents/cowork/executions/${id}/export/`));
    if (!reponse.ok) {
      const corps = await reponse.json().catch(() => ({}));
      throw new Error(corps.detail || "L'export a échoué.");
    }
    const entete = reponse.headers.get('content-disposition') || '';
    const utf8 = entete.match(/filename\*=UTF-8''([^;]+)/i);
    const ascii = entete.match(/filename="?([^";]+)"?/);
    const nomServeur = utf8 ? decodeURIComponent(utf8[1]) : ascii?.[1];

    const blob = await reponse.blob();
    const url = window.URL.createObjectURL(blob);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = nomServeur || `${nomRepli}.${extensionDuType(reponse)}`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    window.URL.revokeObjectURL(url);
  },
};
